<page
  name="dashboard.portfolio"
  description="settings.equityPortfolioDescription"
  headerType="h2"
  productBadge="ecf"
>
  <section [class.is-loading]="isLoading">
    <div class="tw-grid tw-gap-6 sm:tw-grid-cols-2">
      <!-- Total cost basis -->
      <div
        class="tw-flex tw-flex-col tw-justify-center tw-gap-1 tw-rounded-2xl tw-p-6 tw-shadow-[0px_4px_6px_rgba(16,24,40,0.05)] tw-bg-[linear-gradient(90deg,#B61818_16%,#670608_88%)]"
      >
        <span
          class="tw-flex tw-items-center tw-gap-2.5 tw-text-base tw-font-semibold tw-uppercase tw-tracking-[1px] tw-text-[#D1D5DC]"
        >
          {{ 'portfolio.totalCostBasis' | translate }}

          <button
            type="button"
            #tooltipCostBasis="matTooltip"
            (click)="tooltipCostBasis.toggle()"
            class="btn-transparent"
            matTooltip="{{ 'portfolio.grossInvestmentTooltip' | translate }}"
          >
            <div class="pitchin-icon pitchin-icon-help-circle-light p-help-icon--sm"></div>
          </button>
        </span>
        <span class="p-skeleton-bar tw-whitespace-nowrap tw-text-3xl tw-font-bold tw-text-white">{{
          (portfolioSummary?.totalCostBasis ?? portfolioSummary?.grossInvestment) | numberSuffix: { numberFormat: '1.2-2' }
        }}</span>
      </div>

      <!-- Total indicative value -->
      <div
        class="tw-flex tw-flex-col tw-justify-center tw-gap-1 tw-rounded-2xl tw-p-6 tw-shadow-[0px_4px_6px_rgba(16,24,40,0.05)] tw-bg-[linear-gradient(141deg,#364153_23%,#101828_77%)]"
      >
        <span
          class="tw-flex tw-items-center tw-gap-2.5 tw-text-base tw-font-semibold tw-uppercase tw-tracking-[1px] tw-text-[#D1D5DC]"
        >
          {{ 'portfolio.totalMarketValue' | translate }}

          <button
            type="button"
            #tooltipTotalMarket="matTooltip"
            (click)="tooltipTotalMarket.toggle()"
            class="btn-transparent"
            matTooltip="{{ 'portfolio.totalMarketValueTooltip' | translate }}"
          >
            <div class="pitchin-icon pitchin-icon-help-circle-light p-help-icon--sm"></div>
          </button>
        </span>
        <!-- Value + gain/loss badge on one line; the badge wraps below only when the value is wide. -->
        <div class="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-x-4 tw-gap-y-2">
          <span class="p-skeleton-bar tw-whitespace-nowrap tw-text-3xl tw-font-bold tw-text-white">{{
            portfolioSummary?.totalMarketValue | numberSuffix: { numberFormat: '1.2-2' }
          }}</span>
          @if (portfolioSummary?.deltaChanges) {
          <div
            class="p-skeleton-hidden tw-flex tw-items-center tw-gap-2 max-md:tw-flex-col max-md:tw-items-center max-md:tw-gap-1 tw-whitespace-nowrap tw-text-xs"
          >
            <span
              class="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2.5 tw-py-1 tw-font-semibold"
              [ngClass]="{
                'tw-bg-primary-light tw-text-primary': portfolioSummary.deltaChanges < 0,
                'tw-bg-secondary-lightest tw-text-secondary-light': portfolioSummary.deltaChanges > 0,
              }"
            >
              @if (portfolioSummary.deltaChanges > 0) {
                <i class="fa fa-arrow-up" aria-hidden="true"></i>
              }
              @if (portfolioSummary.deltaChanges < 0) {
                <i class="fa fa-arrow-down" aria-hidden="true"></i>
              }
              {{ portfolioSummary.deltaChanges | number: '1.0-2' }}%
            </span>
            <span
              class="tw-text-[15px] tw-font-bold"
              [class.tw-text-secondary-light]="portfolioSummary.deltaChanges > 0"
              [class.tw-text-primary]="portfolioSummary.deltaChanges < 0"
            >
              {{ portfolioSummary.amountChanges | numberSuffix: { numberFormat: '1.2-2' } }}
            </span>
          </div>
          }
        </div>
      </div>
    </div>
  </section>

  @if (!portfolioSummary?.provisional) {
    <div class="tw-mb-5 tw-mt-5">
      <ul class="p-tab">
        <li
          class="p-tab__item max-lg:tw-flex-1 max-lg:tw-text-center"
          [class.active]="selectedTab === 'portfolio'"
        >
          <a class="p-tab__link" [routerLink]="['/dashboard/portfolio']">{{
            'portfolio.holdings' | translate
          }}</a>
        </li>
        <li
          class="p-tab__item max-lg:tw-flex-1 max-lg:tw-text-center"
          [class.active]="selectedTab === 'history'"
        >
          <a
            class="p-tab__link"
            [routerLink]="['/dashboard/portfolio']"
            [queryParams]="{ selectedTab: 'history' }"
            >{{ 'portfolio.history' | translate }}</a
          >
        </li>
      </ul>
    </div>
  }
  <!-- provisional -->
  @if (shareLinkBanner; as reminder) {
    <div
      [ngClass]="
        'share-linking-banner tw-mt-5 ' +
        (linkStatus == ShareLinkStatus.Completed ? 'completed' : 'pending')
      "
    >
      <div class="icon-wrapper">
        <div
          [ngClass]="
            'icon ' +
            (linkStatus == ShareLinkStatus.Completed
              ? 'pitchin-icon-shield-green'
              : 'pitchin-icon-shield-red')
          "
        ></div>
      </div>
      <div class="hint tw-flex-1">
        <div class="header">
          {{ reminder?.bannerText.title | translate }}
        </div>
        <p class="body tw-text-sm">
          {{ reminder?.bannerText.bodyText | translate }}
        </p>
      </div>
      <button
        [routerLink]="linkStatus == ShareLinkStatus.Completed ? '/pstx' : '/proceed-e-kyc'"
        type="button"
        class="button btn tw-whitespace-nowrap max-md:tw-w-full"
      >
        {{ reminder?.buttonText | translate }}
      </button>
    </div>
  }

  <!-- Wait for portfolioListModel before mounting: MySharesComponent self-fetches from the legacy
       /details endpoint when it has no input data, which would race this page's detailsGl request
       and could leave the table on legacy rows with no realised gain (FRC-893). -->
  @if (selectedTab === 'portfolio' && portfolioListModel) {
    <app-my-shares
      [portfolioListModel]="portfolioListModel"
      [total]="total"
      [pageSize]="params.take"
      [provisional]="portfolioSummary?.provisional"
    ></app-my-shares>
  }

  @if (selectedTab === 'history') {
    <app-history> </app-history>
  }

  <!-- Hide for Phase 2 -->
  <!-- <div>
    <div class="col-lg-8 mb-3">
      <div class="chart-panel">
        <div class="d-flex">
          <div class="chart-title w-75">{{ 'portfolio.capitalDeploymentOverTime' | translate }}</div>
          <div class="chart-filter">
            <div class="input-field date-input">
              <input matInput class="form-control" [matDatepicker]="selectedYearPicker"
                [(ngModel)]="selectedYear">
                <mat-datepicker-toggle matSuffix [for]="selectedYearPicker">
                  <div class="pitchin-icon pitchin-icon-calendar" matDatepickerToggleIcon></div>
                </mat-datepicker-toggle>
                <mat-datepicker #selectedYearPicker startView="multi-year"
                (yearSelected)="chosenYearHandler($event, selectedYearPicker)"></mat-datepicker>
              </div>
            </div>
          </div>
          <canvas baseChart width="400" height="200" [datasets]="lineChartData" [labels]="lineChartLabels"
            [options]="lineChartOptions" [colors]="lineChartColors" [legend]="false" chartType="line">
          </canvas>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="chart-panel">
          <div class="chart-title">{{ 'portfolio.investmentCategory' | translate }}</div>
          <canvas baseChart [data]="pieChartData" [labels]="pieChartLabels" chartType="pie" height="200" width="200"
            [options]="pieChartOptions" [legend]="true" [colors]="pieChartColors">
          </canvas>
        </div>
      </div>
    </div> -->
</page>

<div class="container-fluid">
  <app-json-ld [itemLd]="portfolioFaqLd"></app-json-ld>
  <app-faq-accordion [accordionItems]="pageFaqs"></app-faq-accordion>

  <div class="tw-my-5">
    <div class="tw-mb-1 tw-font-bold">
      {{ 'common.disclaimer' | translate }}
    </div>
    <div class="tw-text-sm">
      {{ 'common.disclaimerContent' | translate }}
    </div>
  </div>
</div>
